
import React, { useEffect, useState } from "react";
import { FileText, Pen, CalendarDays, SortDesc } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

// Simple type for order_notes
interface OrderNote {
  id: number;
  order_id: number;
  note: string;
  user_id?: number;
  created_at: string;
}

interface OrderNotesTabProps {
  orderId: number;
}

export const OrderNotesTab: React.FC<OrderNotesTabProps> = ({ orderId }) => {
  const [notes, setNotes] = useState<OrderNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState("");
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    async function fetchNotes() {
      setLoading(true);
      const { data, error } = await supabase
        .from("order_notes")
        .select("*")
        .eq("order_id", orderId)
        .order("created_at", { ascending: false });
      if (error) {
        toast({
          variant: "destructive",
          title: "Failed to load notes",
          description: error.message,
        });
        setNotes([]);
      } else {
        setNotes(data || []);
      }
      setLoading(false);
    }
    fetchNotes();
  }, [orderId, toast]);

  async function handleAddNote() {
    if (!newNote.trim()) return;
    setSaving(true);
    const { data, error } = await supabase
      .from("order_notes")
      .insert({ order_id: orderId, note: newNote.trim() })
      .select()
      .maybeSingle();
    if (error) {
      toast({
        variant: "destructive",
        title: "Failed to add note",
        description: error.message,
      });
    } else if (data) {
      setNotes(prev => [data, ...prev]);
      setNewNote("");
      toast({
        title: "Note added",
        description: "Your note has been added to the order.",
      });
    }
    setSaving(false);
  }

  return (
    <div>
      <div className="mb-6 border-b pb-4">
        <h3 className="text-lg flex items-center gap-2 font-semibold">
          <Pen className="h-5 w-5" /> Order Notes
        </h3>
        <div className="flex flex-col sm:flex-row gap-3 mt-4">
          <Textarea
            placeholder="Add a new note about this order…"
            value={newNote}
            onChange={e => setNewNote(e.target.value)}
            className="sm:w-2/3"
            minLength={1}
            maxLength={4000}
            disabled={saving}
          />
          <Button
            variant="outline"
            className="h-10 self-end"
            disabled={saving || !newNote.trim()}
            onClick={handleAddNote}
          >
            {saving ? "Saving..." : "Add Note"}
          </Button>
        </div>
      </div>
      <div>
        {loading ? (
          <div className="text-gray-500 py-8 text-center">Loading notes…</div>
        ) : notes.length === 0 ? (
          <div className="text-muted-foreground flex flex-col items-center py-12">
            <FileText className="h-8 w-8 mb-2" />
            <span>No notes for this order yet.</span>
          </div>
        ) : (
          <ul className="space-y-4">
            {notes.map((note) => (
              <li key={note.id} className="border rounded-md p-3 bg-muted/30">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Pen className="h-4 w-4 text-brand-blue" />
                    <span className="font-medium text-sm">Note</span>
                  </div>
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <CalendarDays className="h-3 w-3 mr-1" />
                    {new Date(note.created_at).toLocaleString()}
                  </span>
                </div>
                <p className="mt-2 text-sm">{note.note}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
